import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import { RouteNames, adminUserPath } from '../components/AppRouter';
import { apiService } from '../services/axios';
import { useAuth } from '../provider/AuthProvider';

function AdminUserEditPage() {
    const { id } = useParams();
    const { token, isReady } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [erreur, setErreur] = useState(null);
    const [enregistrement, setEnregistrement] = useState(false);

    useEffect(() => {
        if (!isReady || !token) {
            return undefined;
        }

        let annule = false;
        setIsLoading(true);

        apiService.get(`/api/admin/users/${id}`, isReady, token)
            .then((response) => {
                if (annule) {
                    return;
                }
                // Seuls les champs éditables entrent dans le formulaire.
                setForm({
                    email: response.data.email,
                    firstName: response.data.firstName,
                    active: response.data.active,
                });
            })
            .catch((error) => {
                if (annule) {
                    return;
                }
                setErreur(error.response?.status === 404
                    ? 'Cet utilisateur n\'existe pas.'
                    : 'Impossible de récupérer cet utilisateur.');
            })
            .finally(() => { if (!annule) setIsLoading(false); });

        return () => { annule = true; };
    }, [id, isReady, token]);

    const change = (champ, valeur) => setForm((precedent) => ({ ...precedent, [champ]: valeur }));

    const submit = async (event) => {
        event.preventDefault();
        setErreur(null);
        setEnregistrement(true);

        try {
            await apiService.put(`/api/admin/users/${id}`, form, isReady, token);
            navigate(adminUserPath(id));
        } catch (error) {
            // L'API renvoie un message exploitable sur les conflits (email déjà
            // pris, auto-désactivation) et sur les erreurs de validation.
            setErreur(error.response?.data?.message
                ?? error.response?.data?.detail
                ?? 'L\'enregistrement a échoué.');
        } finally {
            setEnregistrement(false);
        }
    };

    return (
        <div className="hero-body is-align-items-flex-start">
            <div className="container">
                <AdminBreadcrumb trail={[
                    { libelle: 'Utilisateurs', to: RouteNames.adminUsers },
                    { libelle: `#${id}`, to: adminUserPath(id) },
                    { libelle: 'Édition' },
                ]} />

                <h1 className="title is-2">Éditer l'utilisateur</h1>

                {isLoading && <p className="has-text-grey">Chargement…</p>}
                {erreur && <div className="notification is-danger is-light">{erreur}</div>}

                {form && (
                    <form onSubmit={submit}>
                        <div className="field">
                            <label className="label" htmlFor="firstName">Prénom</label>
                            <div className="control">
                                <input
                                    id="firstName"
                                    className="input"
                                    type="text"
                                    maxLength={50}
                                    value={form.firstName}
                                    onChange={(e) => change('firstName', e.target.value)}
                                    required
                                    disabled={enregistrement}
                                />
                            </div>
                        </div>

                        <div className="field">
                            <label className="label" htmlFor="email">Email</label>
                            <div className="control">
                                <input
                                    id="email"
                                    className="input"
                                    type="email"
                                    maxLength={180}
                                    value={form.email}
                                    onChange={(e) => change('email', e.target.value)}
                                    required
                                    disabled={enregistrement}
                                />
                            </div>
                        </div>

                        <div className="field">
                            <div className="control">
                                <label className="checkbox">
                                    <input
                                        type="checkbox"
                                        checked={form.active}
                                        onChange={(e) => change('active', e.target.checked)}
                                        disabled={enregistrement}
                                    />
                                    {' '}Compte actif
                                </label>
                            </div>
                            <p className="help">
                                Un compte inactif ne peut plus se connecter. Vous ne pouvez pas
                                désactiver le vôtre.
                            </p>
                        </div>

                        <div className="buttons">
                            <button
                                type="submit"
                                className={`button is-dark ${enregistrement ? 'is-loading' : ''}`}
                                disabled={enregistrement}
                            >
                                Enregistrer
                            </button>
                            <Link className="button is-light" to={adminUserPath(id)}>
                                Annuler
                            </Link>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

export default AdminUserEditPage;
